import React, { ReactNode, useState } from 'react';
import { Button } from "@/ui-components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from "@/ui-components/ui/dialog";
import { 
  Loader2, 
  Trash2,
  FileText
} from "lucide-react";
import { StandardDialogHeader } from "@/util-components/dialog/components/StandardDialogHeader";

interface StandardDeleteDialogProps {
  /**
   * The trigger element that opens the dialog (optional)
   */
  trigger?: ReactNode;
  /**
   * Whether the dialog is open (controlled mode)
   */
  open?: boolean;
  /**
   * Callback when dialog open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Title of the delete dialog
   * @default "Element löschen"
   */
  title?: string;
  /**
   * Description of the delete dialog
   * @default "Diese Aktion kann nicht rückgängig gemacht werden"
   */
  description?: string;
  /**
   * The name/title of the item being deleted (displayed in a detail box)
   */
  itemTitle?: string;
  /**
   * Label for the item type (e.g. "Template ID", "Typ")
   */
  itemTypeLabel?: string;
  /**
   * Value of the item type (e.g. the ID or mimetype)
   */
  itemTypeValue?: string;
  /**
   * Array of items to delete (for bulk deletion)
   */
  itemsList?: Array<{
    title: string;
    subtitle?: string;
  }>;
  /**
   * Async function to execute the deletion
   */
  onDelete: () => Promise<void>;
  /**
   * Whether the deletion is currently in progress
   */
  isLoading?: boolean;
  /**
   * Optional custom content to display below the item info
   */
  children?: ReactNode;
}

/**
 * Standardized Dialog component for deleting items.
 */
export const StandardDeleteDialog: React.FC<StandardDeleteDialogProps> = ({
  trigger,
  open: externalOpen,
  onOpenChange,
  title = "Element löschen",
  description = "Diese Aktion kann nicht rückgängig gemacht werden",
  itemTitle,
  itemTypeLabel,
  itemTypeValue,
  itemsList,
  onDelete,
  isLoading = false,
  children,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const handleDelete = async () => {
    await onDelete();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && (
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
      )}
      
      <DialogContent className="sm:max-w-lg border-0 shadow-2xl p-0 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] left-0 top-0 translate-x-0 translate-y-0 max-w-none max-h-none h-[100vh] w-screen flex flex-col overflow-hidden gap-0 rounded-none sm:left-[50%] sm:top-[50%] sm:h-auto sm:w-full sm:max-h-[85vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:pt-0 sm:pb-0">
        <div className="px-5 sm:px-6 py-4 sm:py-5 shrink-0">
          <StandardDialogHeader
            icon={Trash2}
            title={title}
            description={description}
            variant="red"
            className="p-0 border-none"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 pt-2 space-y-5">
          {/* Item Info */}
          {itemsList && itemsList.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Zu löschende Elemente ({itemsList.length})
                </span>
              </div>
              <div className="bg-gray-50/50 rounded-xl border border-gray-100 overflow-hidden max-h-[200px] overflow-y-auto">
                <ul className="divide-y divide-gray-200">
                  {itemsList.map((item, idx) => (
                    <li key={idx} className="p-3">
                      <p className="text-sm text-gray-700 font-medium">{item.title}</p>
                      {item.subtitle && (
                        <p className="text-xs text-gray-500 mt-1">{item.subtitle}</p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (itemTitle || itemTypeValue) ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Zu löschendes Element
                </span>
              </div>
              <div className="p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                {itemTitle && <p className="text-sm text-gray-700 font-medium">{itemTitle}</p>}
                {itemTypeLabel && itemTypeValue && (
                  <p className="text-xs text-gray-500 mt-1">
                    {itemTypeLabel}: {itemTypeValue}
                  </p>
                )}
              </div>
            </div>
          ) : null}
          
          {children}
        </div>

        <DialogFooter className="px-5 sm:px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-50 shrink-0 bg-background">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isLoading}
            className="h-11 px-5 sm:px-6 rounded-xl font-bold text-sm border-gray-200 text-gray-700 hover:bg-gray-50 transition-all"
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
            className="h-11 px-6 sm:px-8 rounded-xl font-bold text-sm bg-red-600 hover:bg-red-700 text-white shadow-sm transition-all min-w-[140px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Löschen...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Endgültig löschen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
